/**
 * The posting form's *how do you want prices?* choice — `W6-T06`. Presupuestos, or an auction with a
 * duration and an optional ceiling.
 *
 * Pure, for `features/jobs/posting.ts`'s reason. Keys, never sentences.
 */
import { type AuctionPublishInput } from '@marketplace/contracts';
import { eurosToCents } from '../jobs/posting.js';

/**
 * The durations the form offers, in days. The operator's presets (2026-10-01). The server's
 * `AUCTION_DURATIONS_DAYS` is the authority and refuses anything else; this is what is offered.
 */
export const AUCTION_DURATIONS = [1, 3, 7] as const;

/**
 * How long the client has to choose after close — the server's `AUCTION_AWARD_WINDOW_DAYS`
 * (operator: 7). Only used to *say* the date; the server expires the auction on its own clock.
 */
export const AWARD_WINDOW_DAYS = 7;

export type AuctionFormField = 'duration' | 'maxPrice';

export type AuctionChoice = { mode: 'QUOTES' } | { mode: 'AUCTION'; input: AuctionPublishInput };

export type AuctionChoiceResult =
  | { ok: true; choice: AuctionChoice }
  | { ok: false; errors: Partial<Record<AuctionFormField, string>> };

function text(form: FormData, name: string): string {
  const value = form.get(name);
  return typeof value === 'string' ? value.trim() : '';
}

export function readAuctionChoice(form: FormData): AuctionChoiceResult {
  if (text(form, 'priceMode') !== 'AUCTION') return { ok: true, choice: { mode: 'QUOTES' } };

  const errors: Partial<Record<AuctionFormField, string>> = {};

  const days = Number(text(form, 'durationDays'));
  const durationDays = (AUCTION_DURATIONS as readonly number[]).includes(days) ? days : undefined;
  if (durationDays === undefined) errors.duration = 'auctionForm.error.duration';

  // Optional: no ceiling is a legitimate choice, and the commonest one.
  const typed = text(form, 'maxPrice');
  const maxPriceCents = typed === '' ? null : eurosToCents(typed);
  if (maxPriceCents === undefined || maxPriceCents === 0) {
    errors.maxPrice = 'auctionForm.error.maxPrice';
  }

  if (durationDays === undefined || maxPriceCents === undefined || Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }
  return {
    ok: true,
    choice: {
      mode: 'AUCTION',
      input: { durationDays, ...(maxPriceCents === null ? {} : { maxPriceCents }) },
    },
  };
}
